import type { AccountApiResponse } from '@/types/accountApiType';

import { saleorAuthClient } from '@/graphql/client';
import type { CountryCode, LanguageCodeEnum } from '@/graphql/saleor/api';
import { getBrandConfig } from '@/lib/configuration/configuration';
import { paths } from '@/lib/paths';

export type MailingPreferencesEnum =
  | 'Novelties'
  | 'Squad Updates'
  | 'News About Breitling'
  | 'Breitling Events'
  | 'Special Editions'
  | 'Breitling Trade';

export type NewsLetterSubscription = {
  activeSubscription: boolean;
  guest?: boolean;
  languageCode: LanguageCodeEnum;
  mailingPreferences: MailingPreferencesEnum[];
  salesforce_uid?: string;
  uid: string;
  websiteCountryCode: CountryCode;
};

export type NewsLetterUnSubscription = {
  email: string;
};

export type SubscribeCampaignNewsLetterParams = {
  email: string;
  first_name: string;
  form_subscription_type: string;
  language_code: string;
  last_name: string;
  phone: string;
  phone_prefix: string;
  recaptcha_response: string;
  result_page: string;
  salutation: string;
  website_country_code: string;
};

export enum CAMPAIGN_SUBSCRIBE_RESPONSE_CODE {
  Success = 200,
  AlreadySubscribed = 610,
}

export const useNewsLetter = (authClient = saleorAuthClient) => {
  const getNewsLetterSubscriptionStatus = async (): Promise<
    AccountApiResponse<NewsLetterSubscription>
  > => {
    try {
      const response = await authClient.fetchWithAuth(
        paths.api.newsLetter.user,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          cache: 'no-store',
        },
        { allowPassingTokenToThirdPartyDomains: true },
      );

      return response.json();
    } catch (error) {
      return {
        code: -1,
        msg: 'Failed to get the subscribe status of newsletter',
      };
    }
  };

  const subscribeNewsLetterForUser = async ({
    languageCode,
    preferences = null,
    title,
    websiteCountryCode,
  }: any): Promise<AccountApiResponse<NewsLetterSubscription>> => {
    const params = {
      websiteCountryCode,
      languageCode,
      mailingPreferences: preferences, // null for all events, [] for empty events.
      activeSubscription: true,
      title: title,
    };

    try {
      const response = await authClient.fetchWithAuth(
        paths.api.newsLetter.user,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          cache: 'no-store',
          body: JSON.stringify(params),
        },
        { allowPassingTokenToThirdPartyDomains: true },
      );
      return response.json();
    } catch (error) {
      return { code: -1, msg: 'Failed to subscribe to newsletter' };
    }
  };

  const subscribeNewsLetterForGuest = async ({
    countryLanguageSlug,
    email,
    firstName,
    firstNameKanji,
    languageCode,
    lastName,
    lastNameKanji,
    phone,
    phonePrefix,
    recaptchaResponse,
    title,
    websiteCountryCode,
  }: any): Promise<AccountApiResponse<NewsLetterSubscription>> => {
    const params = {
      websiteCountryCode,
      languageCode,
      salutation: title,
      firstName,
      lastName,
      email,
      recaptchaResponse: recaptchaResponse ?? '', // may need replace to backend recaptcha validation
      url_country_language_slug: countryLanguageSlug,
      ...(!!firstNameKanji && { firstNameKanji }),
      ...(!!lastNameKanji && { lastNameKanji }),
      ...(!!phonePrefix && { phonePrefix }),
      ...(!!phone && { phone }),
    };

    const url =
      getBrandConfig().newsletter?.guestApiUrl ??
      paths.api.newsLetter.guestRequest;

    try {
      const response = await authClient.fetchWithAuth(
        url,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          cache: 'no-store',
          body: JSON.stringify(params),
        },
        { allowPassingTokenToThirdPartyDomains: true },
      );
      return response.json();
    } catch (error) {
      return { code: -1, msg: 'Failed to subscribe to newsletter' };
    }
  };

  const getNewsLetterSubscriptionGuest = async (
    emailToken: string,
  ): Promise<AccountApiResponse<NewsLetterSubscription>> => {
    try {
      const params = { emailToken };
      const response = await authClient.fetchWithAuth(
        `${paths.api.newsLetter.guestSubmit}${emailToken}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(params),
          cache: 'no-store',
        },
        { allowPassingTokenToThirdPartyDomains: true },
      );
      return response.json();
    } catch (error) {
      return {
        code: -1,
        msg: 'Failed to get the subscribe status of newsletter',
      };
    }
  };

  const confirmNewsLetterSubscription = async (
    emailToken: string,
  ): Promise<AccountApiResponse<NewsLetterSubscription>> => {
    try {
      const params = { emailToken };
      const response = await authClient.fetchWithAuth(
        paths.api.newsLetter.guestSubmit,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(params),
          cache: 'no-store',
        },
        { allowPassingTokenToThirdPartyDomains: true },
      );
      return response.json();
    } catch (error) {
      return {
        code: -1,
        msg: 'Failed to get the subscribe status of newsletter',
      };
    }
  };

  const unSubscribeNewsLetterForUser = async ({
    email,
  }: {
    email: string;
  }): Promise<AccountApiResponse<NewsLetterUnSubscription>> => {
    try {
      const response = await authClient.fetchWithAuth(
        `${paths.api.newsLetter.unsubscribe}${email}`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
          },
          cache: 'no-store',
        },
        { allowPassingTokenToThirdPartyDomains: true },
      );
      return response.json();
    } catch (error) {
      return { code: -1, msg: 'Failed to unsubscribe to newsletter' };
    }
  };

  const subscribeCampaignNewsLetter = async (
    params: SubscribeCampaignNewsLetterParams,
  ): Promise<AccountApiResponse<null>> => {
    try {
      const response = await authClient.fetchWithAuth(
        paths.api.newsLetter.formSubscription,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          cache: 'no-store',
          body: JSON.stringify(params),
        },
        { allowPassingTokenToThirdPartyDomains: true },
      );
      return response.json();
    } catch (error) {
      return { code: -1, msg: 'Failed to subscribe to campaign newsletter' };
    }
  };

  return {
    subscribeNewsLetterForUser,
    getNewsLetterSubscriptionStatus,
    subscribeNewsLetterForGuest,
    getNewsLetterSubscriptionGuest,
    confirmNewsLetterSubscription,
    unSubscribeNewsLetterForUser,
    subscribeCampaignNewsLetter,
  };
};

export type UseNewsLetter = ReturnType<typeof useNewsLetter>;
